import type { Ref, RefObject } from 'react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useLatest from 'react-use/lib/useLatest'

import { mergeRefs } from '../utils/mergeRefs'
import { usePopover } from './hooks/usePopover'
import { usePopoverBoundaryRef } from './hooks/usePopoverBoundaryRef'
import { usePopoverParentRef } from './hooks/usePopoverParentRef'
import { PopoverPortal } from './PopoverPortal'
import {
  POPOVER_DEFAULT_POSITIONS,
  POPOVER_EMPTY_STATE,
} from './popoverConstants'
import type {
  ContentLocation,
  ContentLocationGetter,
  PopoverAlign,
  PopoverPosition,
  PopoverState,
} from './popoverTypes'
import { rectsAreEqual } from './utils'

export type PopoverProps<T extends HTMLOrSVGElement = HTMLElement> = {
  isOpen: boolean
  disabled?: boolean
  target: (props: PopoverState & { ref: Ref<T> }) => React.ReactNode
  content: (args: PopoverState) => React.ReactNode
  positions?: PopoverPosition[]
  align?: PopoverAlign
  padding?: number
  reposition?: boolean
  containerClassName?: string
  parentElement?: HTMLElement
  containerStyle?: Partial<CSSStyleDeclaration>
  contentLocation?: ContentLocationGetter | ContentLocation
  boundaryElement?: HTMLElement
  boundaryRef?: RefObject<HTMLElement | null>
  boundaryInset?: number
  boundaryTolerance?: number
  onClickOutside?: (e: MouseEvent) => void
  clickOutsideCapture?: boolean
  ref?: Ref<T>
}

function PopoverInternal({
  isOpen,
  disabled = false,
  target,
  content,
  positions: positionsFromProps = POPOVER_DEFAULT_POSITIONS,
  align = 'center',
  padding = 0,
  reposition = true,
  parentElement: parentElementFromProps,
  boundaryElement: boundaryElementFromProps,
  boundaryRef: boundaryRefFromProps,
  containerClassName = 'popover-container',
  containerStyle,
  contentLocation,
  boundaryInset = 0,
  onClickOutside,
  clickOutsideCapture = false,
  ref: externalRef,
}: PopoverProps) {
  const positionsAsString = positionsFromProps.join(',')
  const positions = useMemo(
    () => positionsAsString.split(',') as PopoverPosition[],
    [positionsAsString]
  )
  const parentElementRef = usePopoverParentRef()
  const boundaryElementRef = usePopoverBoundaryRef()

  const parentElement =
    parentElementFromProps || parentElementRef?.current || window.document.body
  const boundaryElement =
    boundaryElementFromProps ||
    boundaryRefFromProps?.current ||
    boundaryElementRef?.current ||
    parentElement
  const prevIsOpen = useRef(false)
  const prevPositions = useRef<PopoverPosition[] | undefined>(undefined)
  const prevContentLocation = useRef<
    ContentLocation | ContentLocationGetter | undefined
  >(undefined)
  const prevReposition = useRef(reposition)

  const childRef = useRef<HTMLElement | null>(null)

  const [popoverState, setPopoverState] = useState<PopoverState>({
    ...POPOVER_EMPTY_STATE,
    align,
    padding,
    boundaryInset,
  })

  const onPositionPopover = useCallback(
    (popoverState: PopoverState) => setPopoverState(popoverState),
    []
  )

  const { positionPopover, popoverRef, scoutRef } = usePopover({
    isOpen,
    childRef,
    containerClassName,
    parentElement,
    boundaryElement,
    contentLocation,
    positions,
    align,
    padding,
    boundaryInset,
    reposition,
    onPositionPopover,
  })

  useLayoutEffect(() => {
    let shouldUpdate = true
    const updatePopover = () => {
      if (isOpen && shouldUpdate) {
        const childRect = childRef?.current?.getBoundingClientRect()
        const popoverRect = popoverRef?.current?.getBoundingClientRect()
        const boundaryRect = boundaryElement.getBoundingClientRect()
        if (
          childRect != null &&
          popoverRect != null &&
          (!rectsAreEqual(boundaryRect, {
            top: popoverState.boundaryRect.top,
            left: popoverState.boundaryRect.left,
            width: popoverState.boundaryRect.width,
            height: popoverState.boundaryRect.height,
            bottom:
              popoverState.boundaryRect.top + popoverState.boundaryRect.height,
            right:
              popoverState.boundaryRect.left + popoverState.boundaryRect.width,
          }) ||
            !rectsAreEqual(childRect, {
              top: popoverState.childRect.top,
              left: popoverState.childRect.left,
              width: popoverState.childRect.width,
              height: popoverState.childRect.height,
              bottom:
                popoverState.childRect.top + popoverState.childRect.height,
              right: popoverState.childRect.left + popoverState.childRect.width,
            }) ||
            popoverRect.width !== popoverState.popoverRect.width ||
            popoverRect.height !== popoverState.popoverRect.height ||
            popoverState.padding !== padding ||
            popoverState.align !== align ||
            positions !== prevPositions.current ||
            contentLocation !== prevContentLocation.current ||
            reposition !== prevReposition.current)
        ) {
          positionPopover()
        }

        if (positions !== prevPositions.current) {
          prevPositions.current = positions
        }
        if (contentLocation !== prevContentLocation.current) {
          prevContentLocation.current = contentLocation
        }
        if (reposition !== prevReposition.current) {
          prevReposition.current = reposition
        }

        if (shouldUpdate) {
          window.requestAnimationFrame(updatePopover)
        }
      }

      prevIsOpen.current = isOpen
    }

    window.requestAnimationFrame(updatePopover)

    return () => {
      shouldUpdate = false
    }
  }, [
    align,
    boundaryElement,
    contentLocation,
    isOpen,
    padding,
    popoverRef,
    popoverState.align,
    popoverState.boundaryRect.height,
    popoverState.boundaryRect.left,
    popoverState.boundaryRect.top,
    popoverState.boundaryRect.width,
    popoverState.childRect.height,
    popoverState.childRect.left,
    popoverState.childRect.top,
    popoverState.childRect.width,
    popoverState.padding,
    popoverState.popoverRect.height,
    popoverState.popoverRect.width,
    positionPopover,
    positions,
    reposition,
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: isOpen triggers style reapplication when popover visibility changes
  useEffect(() => {
    const popoverElement = popoverRef.current

    Object.assign(popoverElement.style ?? {}, containerStyle)

    return () => {
      Object.keys(containerStyle ?? {}).forEach((key) => {
        delete popoverElement.style[
          key as keyof Omit<typeof containerStyle, 'length' | 'parentRule'>
        ]
      })
    }
  }, [containerStyle, isOpen])

  const handleOnClickOutside = (e: MouseEvent) => {
    if (
      isOpen &&
      !popoverRef.current.contains(e.target as Node) &&
      !childRef.current?.contains(e.target as Node) &&
      // do not close if target was already removed from dom
      document.body.contains(e.target as Node)
    ) {
      onClickOutside?.(e)
    }
  }

  const handleWindowResize = useCallback(() => {
    if (childRef.current) {
      window.requestAnimationFrame(() => positionPopover())
    }
  }, [positionPopover])

  const handleClickOutsideRef = useLatest(handleOnClickOutside)
  useEffect(() => {
    const body = parentElement?.ownerDocument.body ?? document.body
    const clickOutsideHandler = handleClickOutsideRef.current
    body.addEventListener('click', clickOutsideHandler, clickOutsideCapture)
    body.addEventListener(
      'contextmenu',
      clickOutsideHandler,
      clickOutsideCapture
    )
    body.addEventListener('resize', handleWindowResize)
    return () => {
      body.removeEventListener(
        'click',
        clickOutsideHandler,
        clickOutsideCapture
      )
      body.removeEventListener(
        'contextmenu',
        clickOutsideHandler,
        clickOutsideCapture
      )
      body.removeEventListener('resize', handleWindowResize)
    }
  }, [clickOutsideCapture, handleWindowResize, parentElement])

  const renderChild = () =>
    target({ ...popoverState, ref: mergeRefs(externalRef, childRef) })

  const renderPopover = () => {
    if (!isOpen || Boolean(disabled)) {
      return null
    }
    return (
      <PopoverPortal
        element={popoverRef.current}
        scoutElement={scoutRef.current}
        container={parentElement}
      >
        {typeof content === 'function' ? content(popoverState) : content}
      </PopoverPortal>
    )
  }

  return (
    <>
      {renderChild()}
      {renderPopover()}
    </>
  )
}

export function Popover<T extends HTMLOrSVGElement = HTMLElement>(
  props: PopoverProps<T>
) {
  if (typeof window === 'undefined') {
    return props.target({ ...POPOVER_EMPTY_STATE, ref: props.ref as Ref<T> })
  }

  const { target, ref, ...rest } = props
  return (
    <PopoverInternal
      {...rest}
      target={target as PopoverProps['target']}
      ref={ref as Ref<HTMLElement>}
    />
  )
}
